import { Stack, Tabs, Tab, Typography, Box } from '@mui/material';
import React, { useRef, useState, useEffect } from 'react';
import { GridData } from 'components/common/grid';
import useBundleTranslation from 'i18n';
import DerivedField from '../derived-field';
import { DerivedFieldData, JoinFieldConfig, RulesConfig } from 'components/dataset-viewer';
import TrackChangesTab from './TrackChangesTab';
import SimpleFieldsSelectorTable from './SimpleFieldsSelectorTable';
import NumericFieldsSelectorTable from './FieldsSelectorTable';
import { TrackTabUpdateDTO } from '../helpers';

function FieldsSelector({
    textColumnsData,
    numericColumnsData,
    selectedColumns,
    onSelectedChange,
    onAggregationChange,
    datasetId,
    onCreateDerived,
    names,
    json,
    onDerivedAction,
    fieldsLabels,
    multiInstancesMode,
    filter,
    onTrackDataUpdate,
    fieldIds,
    onLeftTabChange,
    dataPreviewResized,
}: {
    textColumnsData: GridData;
    numericColumnsData: GridData;
    selectedColumns: string[];
    onSelectedChange: (keys: string[], checked: boolean) => void;
    onAggregationChange: (fieldId: string, value: string, oldValue: string) => void;
    datasetId: number;
    onCreateDerived: (data: DerivedFieldData) => void;
    names: string[];
    json: string;
    onDerivedAction: (action: 'edit' | 'delete', fieldId: string) => void;
    fieldsLabels: string[];
    multiInstancesMode: boolean;
    filter: RulesConfig;
    onTrackDataUpdate: (value: TrackTabUpdateDTO) => void;
    fieldIds: JoinFieldConfig[];
    onLeftTabChange: (tab: number) => void;
    dataPreviewResized?: number;
}) {
    const { t } = useBundleTranslation(['components/dataset-viewer/dataset_viewer']);
    const containerRef = useRef<HTMLDivElement>(null);
    const [textTableHeight, setTextTableHeight] = useState<number | undefined>(undefined);
    const [numericTableHeight, setNumericTableHeight] = useState<number | undefined>(undefined);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        onLeftTabChange(newValue);
    };

    const tabIndex = filter.leftTab === 'fields' ? 0 : 1;

    useEffect(() => {
        const updateHeight = () => {
            if (containerRef.current) {
                const containerHeight = containerRef.current.clientHeight;
                const paddingTop = 16;
                const paddingBottom = 32;
                const spacing = 8;
                const availableHeight = containerHeight - paddingTop - paddingBottom;

                const ROW_HEIGHT = 32;
                const HEADER_HEIGHT = 32;

                const textRowsCount = textColumnsData.length;
                const numericRowsCount = numericColumnsData.length;

                const textNeededHeight = textRowsCount > 0 ? HEADER_HEIGHT + textRowsCount * ROW_HEIGHT : 0;
                const numericNeededHeight = numericRowsCount > 0 ? HEADER_HEIGHT + numericRowsCount * ROW_HEIGHT : 0;

                const totalNeededHeight =
                    textNeededHeight + numericNeededHeight + (textRowsCount > 0 && numericRowsCount > 0 ? spacing : 0);

                if (totalNeededHeight <= availableHeight) {
                    setTextTableHeight(textNeededHeight > 0 ? textNeededHeight : undefined);
                    setNumericTableHeight(numericNeededHeight > 0 ? numericNeededHeight : undefined);
                } else {
                    if (textNeededHeight <= availableHeight / 2) {
                        setTextTableHeight(textNeededHeight);
                        setNumericTableHeight(availableHeight - textNeededHeight - spacing);
                    } else if (numericNeededHeight <= availableHeight / 2) {
                        setNumericTableHeight(numericNeededHeight);
                        setTextTableHeight(availableHeight - numericNeededHeight - spacing);
                    } else {
                        const halfHeight = Math.floor((availableHeight - spacing) / 2);
                        setTextTableHeight(halfHeight);
                        setNumericTableHeight(halfHeight);
                    }
                }
            }
        };

        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [textColumnsData.length, numericColumnsData.length, dataPreviewResized]);

    return (
        <Stack sx={{ height: 1 }}>
            <Box sx={{ pt: 4, px: 3 }}>
                {multiInstancesMode ? (
                    <Box
                        sx={{
                            borderBottom: 1,
                            borderColor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            overflow: 'visible',
                        }}
                    >
                        <Tabs value={tabIndex} onChange={handleChange} sx={{ flexGrow: 1, mb: '-1px' }}>
                            <Tab label={'Select Fields'} key={'fields'} />
                            <Tab label={'Track Changes'} key={'trackChanges'} />
                        </Tabs>
                        {tabIndex === 0 && (
                            <DerivedField
                                onCreateDerived={onCreateDerived}
                                datasetId={datasetId}
                                names={names}
                                json={json}
                                fieldsLabels={fieldsLabels}
                            />
                        )}
                    </Box>
                ) : (
                    <Stack direction="row" alignItems="flex-end" justifyContent="flex-end" spacing={2}>
                        <Typography variant="heading" width={'100%'}>
                            {t('fields_selector.select_fields')}
                        </Typography>
                        <DerivedField
                            onCreateDerived={onCreateDerived}
                            datasetId={datasetId}
                            names={names}
                            json={json}
                            fieldsLabels={fieldsLabels}
                        />
                    </Stack>
                )}
            </Box>
            <Box ref={containerRef} sx={{ flex: 1, pt: 2, pb: 4, px: 3, overflow: 'hidden' }}>
                {multiInstancesMode && tabIndex === 1 ? (
                    <TrackChangesTab
                        showHistory={filter.history}
                        filter={filter}
                        onTrackDataUpdate={onTrackDataUpdate}
                        fieldIds={fieldIds}
                    />
                ) : (
                    <Stack spacing={1}>
                        <SimpleFieldsSelectorTable
                            data={textColumnsData}
                            selectedColumns={selectedColumns}
                            onSelectedChange={onSelectedChange}
                            type={'text'}
                            maxHeight={textTableHeight}
                        />
                        <NumericFieldsSelectorTable
                            data={numericColumnsData}
                            selectedColumns={selectedColumns}
                            onSelectedChange={onSelectedChange}
                            onAggregationChange={onAggregationChange}
                            onDerivedAction={onDerivedAction}
                            filter={filter}
                            maxHeight={numericTableHeight}
                        />
                    </Stack>
                )}
            </Box>
        </Stack>
    );
}

export default React.memo(FieldsSelector);
